import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import Base64Image from '../components/Base64Image';
import PageHeader from '../components/PageHeader';
import {
  AboutPageContainer,
  Section,
  SectionTitle,
  TwoColumn,
  TextBlock,
  ImageBlock,
  List,
  LeadershipGrid,
  LeaderCard,
  LeaderName,
  LeaderTitle,
  StatsRow,
  StatItem,
} from '../styles/AboutPageStyles';

const CACHE_KEY = 'about_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const AboutPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/about');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load about page data.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !pageData) {
    return (
      <AboutPageContainer>
        <LoadingSpinner />
      </AboutPageContainer>
    );
  }

  if (error && !pageData) {
    return <AboutPageContainer>{error}</AboutPageContainer>;
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const organizationBio = pageData ? pageData.organizationBio : null;
  const mission = pageData ? pageData.mission : null;
  const vision = pageData ? pageData.vision : null;
  const coreBeliefs = pageData ? pageData.coreBeliefs : null;
  const motto = pageData ? pageData.motto : null;
  const history = pageData ? pageData.history : null;
  const founder = pageData ? pageData.founder : null;
  const headquartersAddress = pageData ? pageData.headquartersAddress : null;

  const rawBranches = pageData && Array.isArray(pageData.branches) ? pageData.branches : [];
  const branches = rawBranches.filter(
    (branch) =>
      branch && (branch.name || branch.location || typeof branch.isInternational === 'boolean')
  );

  const churchStructure = pageData ? pageData.churchStructure : null;
  const departmentsRaw =
    churchStructure && Array.isArray(churchStructure.departments)
      ? churchStructure.departments
      : [];
  const departments = departmentsRaw.filter(
    (dept) => dept && (dept.name || dept.type || dept.description)
  );
  const growthStatistics = pageData ? pageData.growthStatistics : null;
  const leadershipTeam =
    pageData && Array.isArray(pageData.leadershipTeam) ? pageData.leadershipTeam : [];

  const renderMultilineText = (text) => {
    if (!text) return null;
    const lines = String(text).split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <AboutPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {organizationBio && (organizationBio.text || organizationBio.image) && (
        <Section>
          <SectionTitle>Who We Are</SectionTitle>
          <TwoColumn>
            {organizationBio.text && (
              <TextBlock>{organizationBio.text}</TextBlock>
            )}
            {organizationBio.image && (
              <ImageBlock>
                <Base64Image
                  image={organizationBio.image}
                  alt={header && header.title ? header.title : 'Organization'}
                  style={{ width: '100%', maxWidth: 420, borderRadius: 8 }}
                />
              </ImageBlock>
            )}
          </TwoColumn>
        </Section>
      )}

      {(mission || vision || coreBeliefs || motto) && (
        <Section>
          <SectionTitle>Mission, Vision & Beliefs</SectionTitle>
          <TextBlock>
            {mission && (
              <p>
                <strong>Mission:</strong>{' '}
                {renderMultilineText(mission)}
              </p>
            )}
            {vision && (
              <p>
                <strong>Vision:</strong>{' '}
                {renderMultilineText(vision)}
              </p>
            )}
            {coreBeliefs && (
              <p>
                <strong>Core Beliefs:</strong>{' '}
                {renderMultilineText(coreBeliefs)}
              </p>
            )}
            {motto && (
              <p><strong>Motto:</strong> {motto}</p>
            )}
          </TextBlock>
        </Section>
      )}

      {history && (history.text || history.image) && (
        <Section>
          <SectionTitle>Our History</SectionTitle>
          <TwoColumn>
            {history.text && (
              <TextBlock>{history.text}</TextBlock>
            )}
            {history.image && (
              <ImageBlock>
                <Base64Image
                  image={history.image}
                  alt="History"
                  style={{ width: '100%', maxWidth: 420, borderRadius: 8 }}
                />
              </ImageBlock>
            )}
          </TwoColumn>
        </Section>
      )}

      {founder && (founder.name || founder.title || founder.bio || founder.image) && (
        <Section>
          <SectionTitle>Founder</SectionTitle>
          <TwoColumn>
            <TextBlock>
              {founder.name && <p><strong>{founder.name}</strong></p>}
              {founder.title && <p>{founder.title}</p>}
              {founder.bio && <p>{founder.bio}</p>}
            </TextBlock>
            {founder.image && (
              <ImageBlock>
                <Base64Image
                  image={founder.image}
                  alt={founder.name || 'Founder'}
                  style={{ width: '100%', maxWidth: 320, borderRadius: 8 }}
                />
              </ImageBlock>
            )}
          </TwoColumn>
        </Section>
      )}

      {(headquartersAddress || branches.length > 0) && (
        <Section>
          <SectionTitle>Locations</SectionTitle>
          {headquartersAddress && (
            <TextBlock style={{ marginBottom: 16 }}>
              <p><strong>Headquarters:</strong> {headquartersAddress}</p>
            </TextBlock>
          )}
          {branches.length > 0 && (
            <List>
              {branches.map((branch, index) => {
                const parts = [];
                if (branch.name) parts.push(branch.name);
                if (branch.location) parts.push(branch.location);
                if (branch.isInternational) parts.push('International');
                const line = parts.join(' | ');
                return <li key={`${branch.name || 'branch'}-${index}`}>{line}</li>;
              })}
            </List>
          )}
        </Section>
      )}

      {departments.length > 0 && (
        <Section>
          <SectionTitle>Church Structure & Departments</SectionTitle>
          <List>
            {departments.map((dept, index) => {
              const parts = [];
              if (dept.name) parts.push(dept.name);
              if (dept.type) parts.push(dept.type);
              if (dept.description) parts.push(dept.description);
              const line = parts.join(' | ');
              return <li key={`${dept.name || 'department'}-${index}`}>{line}</li>;
            })}
          </List>
        </Section>
      )}

      {growthStatistics && (
        <Section>
          <SectionTitle>Growth Statistics</SectionTitle>
          <StatsRow>
            {typeof growthStatistics.membersCount === 'number' && (
              <StatItem>
                <strong>Members:</strong> {growthStatistics.membersCount}
              </StatItem>
            )}
            {typeof growthStatistics.branchesCount === 'number' && (
              <StatItem>
                <strong>Branches:</strong> {growthStatistics.branchesCount}
              </StatItem>
            )}
            {typeof growthStatistics.internationalBranchesCount === 'number' && (
              <StatItem>
                <strong>International Branches:</strong> {growthStatistics.internationalBranchesCount}
              </StatItem>
            )}
            {typeof growthStatistics.foundedYear === 'number' && (
              <StatItem>
                <strong>Founded:</strong> {growthStatistics.foundedYear}
              </StatItem>
            )}
          </StatsRow>
        </Section>
      )}

      {leadershipTeam.length > 0 && (
        <Section>
          <SectionTitle>Leadership Team</SectionTitle>
          <LeadershipGrid>
            {leadershipTeam.map((leader, index) => (
              <LeaderCard key={`${leader.name || 'leader'}-${index}`}>
                {leader.image && (
                  <Base64Image
                    image={leader.image}
                    alt={leader.name || 'Leader'}
                    style={{ width: '100%', borderRadius: 8, marginBottom: 8 }}
                  />
                )}
                {leader.name && <LeaderName>{leader.name}</LeaderName>}
                {leader.title && <LeaderTitle>{leader.title}</LeaderTitle>}
                {leader.bio && <p>{leader.bio}</p>}
              </LeaderCard>
            ))}
          </LeadershipGrid>
        </Section>
      )}
    </AboutPageContainer>
  );
};

export default AboutPage;
